import { DestroyRef, inject, Signal, Type } from '@angular/core';
import { updateState } from '@ngrx-toolkit/core';
import {
  EmptyFeatureResult,
  SignalStoreFeature,
  signalStoreFeature,
  SignalStoreFeatureResult,
  withMethods,
  withState,
  WritableStateSource,
} from '@ngrx/signals';
import {
  addEntity,
  EntityId,
  NamedEntityState,
  removeEntities,
  removeEntity,
  setAllEntities,
  setEntity,
  updateEntity,
  upsertEntities,
} from '@ngrx/signals/entities';
import { CommonPatchHttpRequestPayload } from '@pinnakl/shared/types';
import equal from 'fast-deep-equal';
import cloneDeep from 'lodash/cloneDeep';
import { CallState, getCallStateKeys, toErrorMessage } from './call-state.feature';
import { capitalize } from './store-keys';

export type Filter = Record<string, unknown>;
export type Entity = { id: EntityId };
export type Payload = NonNullable<unknown>;
export type PayloadType<E extends Entity, P> = P extends undefined ? E : P extends Payload ? P : E;

export interface DataService<E extends Entity, F extends Filter, P extends Payload | undefined = undefined> {
  load?(filter: F): Promise<E[] | { items: E[]; totalCount: number; next?: string | null }>;

  loadById?(id: EntityId, filter?: F): Promise<E | undefined>;

  create?(entity: Partial<PayloadType<E, P>>, filter?: F): Promise<E>;

  updateById?(id: EntityId, entity: Partial<PayloadType<E, P>>, filter?: F): Promise<E>;

  patchById?(
    id: EntityId,
    patches: CommonPatchHttpRequestPayload[] | CommonPatchHttpRequestPayload,
    filter?: F,
  ): Promise<E>;

  removeById?(id: EntityId, entity?: Partial<E>, filter?: F): Promise<void>;

  removeMany?(ids: EntityId[], filter?: F): Promise<void>;
}

export { capitalize } from './store-keys';

function normalizeFilter<F extends Filter>(filter: F): F {
  const normalized = cloneDeep(filter);
  if (normalized['cursor'] === null) delete normalized['cursor'];
  return normalized;
}

type ReplacementRequest<E, F> = {
  query: F;
  promise: Promise<E[]>;
};

export function getDataServiceKeys<Collection extends string>({ collection }: { collection: Collection }) {
  if (!collection) throw new Error('withDataService requires a collection name');
  const name = capitalize(collection);
  return {
    filterKey: `${collection}Filter`,
    totalCountKey: `${collection}TotalCount`,
    updateTotalCountKey: `update${name}TotalCount`,
    cursorKey: `${collection}Cursor`,
    updateCursorKey: `update${name}Cursor`,
    updateFilterKey: `update${name}Filter`,
    loadKey: `load${name}Entities`,
    setLoadedKey: `setLoaded${name}Entities`,
    setLoadingKey: `setLoading${name}Entities`,
    setProcessingKey: `setProcessing${name}Entities`,
    setErrorKey: `setError${name}Entities`,
    loadByIdKey: `loadById${name}Entities`,
    currentKey: `current${name}`,
    setCurrentKey: `setCurrent${name}`,
    createKey: `create${name}Entities`,
    addEntityKey: `add${name}Entity`,
    updateByIdKey: `updateById${name}Entities`,
    updateEntityByIdKey: `update${name}EntityById`,
    patchByIdKey: `patchById${name}Entities`,
    removeByIdKey: `removeById${name}Entities`,
    removeEntityByIdKey: `remove${name}EntityById`,
    removeManyKey: `removeMany${name}Entities`,
    entitiesKey: `${collection}Entities`,
    entityMapKey: `${collection}EntityMap`,
    idsKey: `${collection}Ids`,
  } as const;
}

export type NamedDataServiceState<E extends Entity, F extends Filter, Collection extends string> = {
  [K in Collection as `${K}Filter`]: F;
} & {
  [K in Collection as `${K}Cursor`]: string | null;
} & {
  [K in Collection as `current${Capitalize<K>}`]: E | null;
} & {
  [K in Collection as `${K}TotalCount`]: number | null;
};

type CollectionMethods<E extends Entity, F extends Filter, P extends Payload | undefined> = Omit<
  Required<DataService<E, F, P>>,
  'load' | 'loadById'
> & {
  updateFilter: (filter: F) => void;
  updateTotalCount: (totalCount: number) => void;
  updateCursor: (cursor: string | null) => void;
  load: (filter: F, forceRefetch?: boolean, concatItems?: boolean) => Promise<E[]>;
  loadById: (id: EntityId, forceRefetch?: boolean, filter?: F, addToList?: boolean) => Promise<E | undefined>;
  setCurrent: (entity: E | null) => void;
  addEntity: (entity: E) => void;
  updateEntityById: (id: EntityId, entity: Partial<E>) => void;
  removeEntityById: (id: EntityId) => void;
};

type DataServiceKeys<Collection extends string> = ReturnType<typeof getDataServiceKeys<Collection>>;

export type NamedDataServiceMethods<
  E extends Entity,
  F extends Filter,
  Collection extends string,
  P extends Payload | undefined,
> = {
  [K in keyof CollectionMethods<E, F, P> as DataServiceKeys<Collection>[`${K}Key`]]: CollectionMethods<E, F, P>[K];
};

type DataServiceConfig<S, F, Collection extends string> = {
  dataServiceType: Type<S>;
  filter: F;
  collection: Collection;
};

export function withDataService<
  E extends Entity,
  F extends Filter,
  S extends DataService<E, F, P>,
  Collection extends string,
  P extends Payload | undefined = undefined,
>(
  options: DataServiceConfig<S, F, Collection>,
): SignalStoreFeature<
  SignalStoreFeatureResult,
  {
    state: NamedDataServiceState<E, F, Collection>;
    props: EmptyFeatureResult['props'];
    methods: NamedDataServiceMethods<E, F, Collection, P>;
  }
>;
export function withDataService<
  E extends Entity,
  F extends Filter,
  S extends DataService<E, F, P>,
  Collection extends string,
  P extends Payload | undefined = undefined,
>(options: DataServiceConfig<S, F, Collection>): SignalStoreFeature {
  const { dataServiceType, filter, collection } = options;
  const { callStateKey, pendingMutationsKey, updateLoadedOneKey, loadedOneKey, loadedManyKey } = getCallStateKeys({
    collection,
  });
  const {
    entitiesKey,
    filterKey,
    totalCountKey,
    updateTotalCountKey,
    cursorKey,
    updateCursorKey,
    loadKey,
    loadByIdKey,
    currentKey,
    setCurrentKey,
    setLoadingKey,
    setLoadedKey,
    setErrorKey,
    setProcessingKey,
    createKey,
    addEntityKey,
    updateByIdKey,
    patchByIdKey,
    removeByIdKey,
    removeEntityByIdKey,
    updateEntityByIdKey,
    removeManyKey,
    updateFilterKey,
  } = getDataServiceKeys(options);
  return signalStoreFeature(
    withState(() => ({
      [filterKey]: cloneDeep(filter),
      [currentKey]: null as E | null,
      [totalCountKey]: 0,
      [cursorKey]: null,
    })),
    withMethods((store: Record<string, unknown> & WritableStateSource<object>) => {
      const dataService = inject(dataServiceType);
      const create = dataService.create?.bind(dataService);
      const update = dataService.updateById?.bind(dataService);
      const patch = dataService.patchById?.bind(dataService);
      const remove = dataService.removeById?.bind(dataService);
      const removeMany = dataService.removeMany?.bind(dataService);
      const entities = store[entitiesKey] as Signal<E[]>;
      const storedFilter = store[filterKey] as Signal<F>;
      let replacementGeneration = 0;
      let collectionQuery = normalizeFilter(filter);
      let replacement: ReplacementRequest<E, F> | undefined;
      const appendRequests = new Set<symbol>();
      let cachedEntities: E[] | undefined;
      let latestDetailRequest: symbol | undefined;
      let detailCall: symbol | undefined;
      let callStateOwner: symbol | undefined;
      let listCall: symbol | undefined;
      let listFailed = false;
      let listError: unknown;
      let destroyed = false;

      inject(DestroyRef).onDestroy(() => {
        destroyed = true;
        replacementGeneration++;
        replacement = undefined;
        latestDetailRequest = undefined;
        cachedEntities = undefined;
        appendRequests.clear();
      });

      const startCall = (action: string, state: CallState, owner = Symbol()): symbol => {
        callStateOwner = owner;
        if (!destroyed && store[callStateKey]) {
          updateState(store, action, { [callStateKey]: state });
        }
        return owner;
      };

      const finishCall = (owner: symbol | undefined, action: string, state: CallState): void => {
        if (!destroyed && callStateOwner === owner && store[callStateKey]) {
          updateState(store, action, { [callStateKey]: state });
        }
      };

      const finishList = (): void => {
        if (!replacement && !appendRequests.size) {
          finishCall(
            listCall,
            listFailed ? setErrorKey : setLoadedKey,
            listFailed ? { error: toErrorMessage(listError) } : 'loadedMany',
          );
        }
      };

      const changePendingMutations = (delta: number): void => {
        if (!destroyed && store[pendingMutationsKey]) {
          updateState(store, setProcessingKey, (state: Record<string, unknown>) => ({
            [pendingMutationsKey]: (state[pendingMutationsKey] as number) + delta,
          }));
        }
      };

      const mutate = async <R>(
        name: string,
        execute: (() => Promise<R>) | undefined,
        apply: (result: R) => void,
        success: CallState = 'loadedOne',
      ): Promise<R> => {
        if (!execute) throw new Error(`${name} is not implemented`);
        changePendingMutations(1);
        const call = startCall(setProcessingKey, 'processing');
        try {
          const result = await execute();
          if (!destroyed) apply(result);
          finishCall(call, setLoadedKey, success);
          return result;
        } catch (error) {
          finishCall(call, setErrorKey, { error: toErrorMessage(error) });
          throw error;
        } finally {
          changePendingMutations(-1);
        }
      };

      const applyUpdate = (id: EntityId, result: E): void => {
        const updater = updateEntity<Collection, NamedEntityState<E, Collection>, E>(
          { id, changes: result },
          { collection },
        );
        const current = store[currentKey] as Signal<E | null>;
        updateState(store, updateByIdKey, updater, current()?.id === id ? { [currentKey]: result } : {});
      };

      return {
        [updateCursorKey]: (cursor: string | null): void => {
          updateState(store, updateCursorKey, { [cursorKey]: cursor });
        },
        [updateFilterKey]: (filter: F): void => {
          updateState(store, updateFilterKey, { [filterKey]: filter });
        },
        [updateTotalCountKey]: (totalCount: number): void => {
          updateState(store, updateTotalCountKey, { [totalCountKey]: totalCount });
        },
        [updateEntityByIdKey]: (id: EntityId, changes: Partial<E>): void => {
          const updater = updateEntity<Collection, NamedEntityState<E, Collection>, E>({ id, changes }, { collection });
          updateState(store, updateEntityByIdKey, updater);
        },
        [removeEntityByIdKey]: (id: EntityId): void => {
          const updater = removeEntity(id, { collection });
          updateState(store, removeEntityByIdKey, updater);
        },
        [loadKey]: (filter: F, forceRefetch = false, concatItems = false): Promise<E[]> => {
          const load = dataService.load?.bind(dataService);
          if (!load) return Promise.reject(new Error('Load is not implemented'));
          const filterSnapshot = cloneDeep(filter ?? storedFilter());
          const query = normalizeFilter(filterSnapshot);

          if (!concatItems && !forceRefetch && replacement && equal(replacement.query, query)) {
            return replacement.promise;
          }

          if (
            !concatItems &&
            !forceRefetch &&
            !replacement &&
            !appendRequests.size &&
            !listFailed &&
            cachedEntities !== undefined &&
            (!store[loadedManyKey] || (store[loadedManyKey] as Signal<boolean>)()) &&
            !Object.keys(query).length &&
            !Object.keys(normalizeFilter(storedFilter())).length &&
            cachedEntities === entities()
          ) {
            return Promise.resolve(cachedEntities);
          }

          if (!concatItems) {
            replacementGeneration++;
            appendRequests.clear();
          }
          cachedEntities = undefined;
          const generation = replacementGeneration;
          const appendId = Symbol();
          let request: ReplacementRequest<E, F> | undefined;
          const isCurrent = (): boolean =>
            !destroyed && generation === replacementGeneration && (concatItems || replacement === request);

          // Defer execution until its identity and shared promise have been registered.
          const promise = Promise.resolve().then(async (): Promise<E[]> => {
            try {
              const result = await load(cloneDeep(query));
              const page = Array.isArray(result) ? result : result.items;
              if (isCurrent()) {
                if (!concatItems) collectionQuery = query;
                const updater = concatItems
                  ? upsertEntities(page, { collection })
                  : setAllEntities(page, { collection });
                updateState(store, loadKey, updater, {
                  [filterKey]: filterSnapshot,
                  [totalCountKey]: Array.isArray(result) ? null : result.totalCount,
                  [cursorKey]: Array.isArray(result) ? null : (result.next ?? null),
                  ...(store[loadedManyKey] ? { [loadedManyKey]: true } : {}),
                });
                if (isCurrent() && !concatItems && !Object.keys(query).length) {
                  cachedEntities = entities();
                }
              }
              return page;
            } catch (error) {
              if (isCurrent()) {
                listFailed = true;
                listError = error;
              }
              throw error;
            } finally {
              if (isCurrent()) {
                if (concatItems) {
                  appendRequests.delete(appendId);
                } else {
                  replacement = undefined;
                }
                finishList();
              }
            }
          });

          if (concatItems) {
            appendRequests.add(appendId);
          } else {
            request = { query, promise };
            replacement = request;
            if (!equal(collectionQuery, query)) {
              collectionQuery = query;
              const clear = setAllEntities<E, Collection>([], { collection });
              updateState(store, loadKey, clear, {
                [filterKey]: cloneDeep(filterSnapshot),
                [totalCountKey]: 0,
                [cursorKey]: null,
                ...(store[loadedManyKey] ? { [loadedManyKey]: false } : {}),
              });
            }
          }
          if (isCurrent()) {
            listFailed = false;
            listError = undefined;
            listCall = Symbol();
            startCall(setLoadingKey, 'loadingMany', listCall);
          }
          return promise;
        },
        [loadByIdKey]: async (
          id: EntityId,
          forceRefetch = false,
          filter?: F,
          addToList = false,
        ): Promise<E | undefined> => {
          if (!dataService.loadById) {
            throw new Error('Load by id is not implemented');
          }
          const current = store[currentKey] as Signal<E | null>;
          const cached = current();
          if (!forceRefetch && cached?.id === id) {
            latestDetailRequest = undefined;
            finishCall(detailCall, setLoadedKey, 'loadedOne');
            return cached;
          }
          const request = Symbol();
          latestDetailRequest = request;
          const generation = replacementGeneration;
          const call = startCall(setLoadingKey, 'loadingOne');
          detailCall = call;
          const filters = cloneDeep(filter ?? storedFilter());
          try {
            const result = await dataService.loadById(id, filters);
            if (!destroyed && latestDetailRequest === request) {
              updateState(store, loadByIdKey, { [currentKey]: result ?? null });
              finishCall(call, setLoadedKey, 'loadedOne');
              store[loadedOneKey] && updateState(store, updateLoadedOneKey, { [loadedOneKey]: true });
            }
            if (!destroyed && generation === replacementGeneration && addToList && result) {
              const updater = setEntity(result, { collection });
              updateState(store, createKey, updater);
            }
            return result;
          } catch (e) {
            if (latestDetailRequest === request) {
              finishCall(call, setErrorKey, { error: toErrorMessage(e) });
            }
            throw e;
          }
        },
        [setCurrentKey]: (current: E | null): void => {
          latestDetailRequest = undefined;
          finishCall(detailCall, setCurrentKey, current ? 'loadedOne' : 'init');
          updateState(store, setCurrentKey, { [currentKey]: current });
        },
        [createKey]: (data: Partial<PayloadType<E, P>>, filter?: F): Promise<E> =>
          mutate('Create', create && (() => create(data, cloneDeep(filter ?? storedFilter()))), result => {
            const updater = setEntity(result, { collection });
            updateState(store, createKey, updater, { [currentKey]: result });
          }),
        [addEntityKey]: (entityToAdd: E): void => {
          const creator = addEntity(entityToAdd, { collection });
          updateState(store, addEntityKey, creator);
        },
        [updateByIdKey]: (id: EntityId, data: Partial<PayloadType<E, P>>, filter?: F): Promise<E> =>
          mutate('Update', update && (() => update(id, data, cloneDeep(filter ?? storedFilter()))), result =>
            applyUpdate(id, result),
          ),
        [patchByIdKey]: (
          id: EntityId,
          patches: CommonPatchHttpRequestPayload[] | CommonPatchHttpRequestPayload,
          filter?: F,
        ): Promise<E> =>
          mutate('Patch', patch && (() => patch(id, patches, cloneDeep(filter ?? storedFilter()))), result =>
            applyUpdate(id, result),
          ),
        [removeByIdKey]: (id: EntityId, data?: Partial<E>, filter?: F): Promise<void> =>
          mutate('Remove', remove && (() => remove(id, data, cloneDeep(filter ?? storedFilter()))), () => {
            const updater = removeEntity(id, { collection });
            const totalCount = (store[totalCountKey] as Signal<number | null>)();
            updateState(store, removeEntityByIdKey, updater, {
              [totalCountKey]: totalCount !== null && totalCount > 0 ? totalCount - 1 : totalCount,
            });
          }),
        [removeManyKey]: (ids: EntityId[], filter?: F): Promise<void> =>
          mutate(
            'Remove many',
            removeMany && (() => removeMany(ids, cloneDeep(filter ?? storedFilter()))),
            () => {
              const updater = removeEntities(ids, { collection });
              updateState(store, removeManyKey, updater);
            },
            'loadedMany',
          ),
      };
    }),
  );
}
